import { MoonfallCloud } from '../convex-client.js';
/**
 * sound-effects.js
 * The sound-effects (SFX) audio channel — fully independent from music.
 *
 * The slider value IS the actual playback level (no master, no hidden mix):
 * SFX play at `sfxVolume` (default 100%), silenced when the channel is muted.
 * Preferences persist under moonfall_sfx_volume / moonfall_sfx_muted and sync
 * across same-origin documents (another open tab) via storage events.
 */

import { AudioGainBus } from './audio-gain-bus.js';
import { BootVeil } from './boot-veil.js';
import { LoadScheduler } from '../utils/load-scheduler.js';
let SoundEffects = {};

window.__moonfallSoundEffectsDestroy?.();

SoundEffects = (function () {
    const SFX_VOLUME_KEY = 'moonfall_sfx_volume';
    const SFX_MUTED_KEY = 'moonfall_sfx_muted';
    // Retired keys migrated then removed. moonfall_master_volume was the
    // 2026-07-11 single master slider; SFX played at the raw master, so it
    // maps straight onto the new independent SFX level.
    const LEGACY_MASTER_KEY = 'moonfall_master_volume';
    const LEGACY_MUTE_KEY = 'moonfall_sound_effects_muted';
    const OBSOLETE_SFX_VOLUME_KEY = 'moonfall_sfx_volume_legacy';
    const DEFAULT_VOLUME = 1.0;
    const RESTORE_VOLUME = 0.7; // unmuting from a 0 level lands here
    const MAX_DEFERRED_PLAY_MS = 2000;
    const VOICE_POOL_SIZE = 2;
    const VOICE_WARMUP_TIMEOUT_MS = 2500;

    // Per-sound trim on top of the channel volume (1 = as authored). Lets an
    // individually hot/quiet generated sound be tamed without regenerating.
    const soundGains = {};

    const soundPools = {};
    const poolCursors = {};
    const voicePlaybackStates = new WeakMap();
    // A voice may be asked to preload by global boot, a section entry, and a
    // later interaction hint. Calling load() again resets a ready media
    // resource, so each physical voice owns exactly one warm-up lifecycle.
    const voiceWarmStates = new WeakMap();
    // A timeout means the browser may still finish the load in its own time;
    // the next observer reattaches readiness listeners without resetting it.
    // A concrete media error is different and permits one fresh load attempt.
    const voiceWarmRetryLoads = new WeakMap();
    const activeVoiceWarmStates = new Set();
    // Resolved URL per effect, kept so the WebAudio path can decode the same
    // clip the element path streams.
    const soundSources = {};
    let isInitialized = false;
    let usingConvexSounds = false;
    let loadPromise = null;
    const requestedPreloads = new Set();
    let convexReadyListener = null;
    let settleScope = null;
    let destroyed = false;
    let playbackSequence = 0;

    let volume = DEFAULT_VOLUME;
    let muted = false;
    let lastNonZeroVolume = DEFAULT_VOLUME;

    function clamp01(value) {
        const n = Number(value);
        return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : null;
    }

    function readStored(key) {
        try { return localStorage.getItem(key); } catch (_) { return null; }
    }

    function persist() {
        try {
            localStorage.setItem(SFX_VOLUME_KEY, String(volume));
            localStorage.setItem(SFX_MUTED_KEY, String(muted));
        } catch (_) { }
    }

    function effectiveVolume() {
        return muted || volume <= 0 ? 0 : volume;
    }

    function loadPreferences() {
        const storedVolume = readStored(SFX_VOLUME_KEY);
        const storedMuted = readStored(SFX_MUTED_KEY);
        const legacyMaster = readStored(LEGACY_MASTER_KEY);
        const legacyMute = readStored(LEGACY_MUTE_KEY);

        if (storedVolume !== null) {
            const n = clamp01(storedVolume);
            volume = n === null ? DEFAULT_VOLUME : n;
        } else if (legacyMaster !== null) {
            const n = clamp01(legacyMaster);
            volume = n === null ? DEFAULT_VOLUME : n;
        } else {
            volume = DEFAULT_VOLUME;
        }

        muted = storedMuted !== null ? storedMuted === 'true' : legacyMute === 'true';
        if (volume > 0) lastNonZeroVolume = volume;

        // The sound-effects module loads AFTER soundtrack.js, so by now the music
        // channel has already read the master value for its own migration; it is
        // safe to retire the shared master key here.
        try {
            localStorage.removeItem(LEGACY_MASTER_KEY);
            localStorage.removeItem(LEGACY_MUTE_KEY);
            localStorage.removeItem(OBSOLETE_SFX_VOLUME_KEY);
        } catch (_) { }

        persist();
    }

    function applyVolumeToLoadedSounds() {
        const eff = effectiveVolume();
        for (const pool of Object.values(soundPools)) {
            for (const sound of pool || []) {
                if (sound) sound.volume = eff;
            }
        }
    }

    function dispatchChanged() {
        document.dispatchEvent(new CustomEvent('moonfall:sfx-changed', {
            detail: { volume, muted, effective: effectiveVolume() }
        }));
    }

    function commit({ persistPref = true } = {}) {
        applyVolumeToLoadedSounds();
        if (persistPref) persist();
        dispatchChanged();
    }

    function setVolume(value) {
        const n = clamp01(value);
        if (n === null) return;
        volume = n;
        if (n > 0) {
            muted = false;
            lastNonZeroVolume = n;
        } else {
            muted = true;
        }
        commit();
    }

    function setMuted(value) {
        muted = Boolean(value);
        commit();
    }

    function toggleMuted() {
        if (muted || volume <= 0) {
            muted = false;
            if (volume <= 0) {
                volume = lastNonZeroVolume > 0 ? lastNonZeroVolume : RESTORE_VOLUME;
            }
        } else {
            muted = true;
        }
        commit();
        return muted;
    }

    function getVolume() {
        return volume;
    }

    function getEffectiveVolume() {
        return effectiveVolume();
    }

    function isMuted() {
        return muted || volume <= 0;
    }

    // Keep the channel in sync across same-origin documents — another tab of
    // the game changing the slider. Apply the incoming value WITHOUT
    // re-persisting so we never bounce a storage event back.
    function onStorage(event) {
        if (!event) return;
        if (event.key === SFX_VOLUME_KEY) {
            const n = clamp01(event.newValue);
            if (n === null) return;
            volume = n;
            if (n > 0) lastNonZeroVolume = n;
            commit({ persistPref: false });
        } else if (event.key === SFX_MUTED_KEY) {
            muted = event.newValue === 'true';
            commit({ persistPref: false });
        }
    }

    const soundFiles = {
        press: 'press.mp3',
        pack_open: 'pack_open.mp3',
        card_reveal: 'card_reveal.mp3',
        draw: 'draw.mp3',
        play: 'play.mp3',
        summon: 'summon.mp3',
        magic: 'magic.mp3',
        stellar: 'stellar.mp3',
        damage: 'damage.mp3',
        heal: 'heal.mp3',
        win: 'win.mp3',
        lose: 'lose.mp3',
        disconnect: 'disconnect.mp3',
        success: 'success.mp3',
        manga_reveal: 'manga_reveal.mp3',
        turn_start: 'turn_start.mp3',
        error: 'error.mp3',
        impact: 'impact.mp3',
        destroy: 'destroy.mp3',
        forge_reveal: 'forge_reveal.mp3'
    };

    /* `card_reveal` is here because it is the FIRST-RUN REWARD CUE.
       The 500-Stardust screen plays it seconds after sign-in and gives it a
       bounded 900ms readiness grace before dropping it. Cold, that window has
       to cover a Convex storage-URL lookup, a fetch and a decode while the
       lobby is also pulling cards and portraits — so it routinely missed and
       the most important celebration in the product arrived silent
       ("the login flow had no sound when i was awarded stardust").
       The grace was the band-aid; warming the cue is the fix.
       Contract: tests/unit/ui/rewardCueBootWarm.test.js */
    const BOOT_PRELOAD = ['press', 'success', 'error', 'card_reveal'];
    /* THE FIRST SCREEN LOADS FIRST; THE CUES FOLLOW IT (round 15, 28
       September 2026). Traced on the Chrome phone over 4G (guest tutorial,
       cold): the interface cues started 480 ms after the first screen's two
       ~520 KB portraits, and the thirteen battle cues the moment the mulligan
       presented at its cap, with a portrait and a card still on the wire. Only
       the press tick plays on a first screen (every control makes it, the
       mulligan's cards too), so it loads with the screen; every other warm
       waits until the first screen has settled (afterFirstScreen). The boot
       list above is unchanged: the reward cue is still warm long before the
       reward (a new account's first screen is the sign-in card). */
    const FIRST_SCREEN_CUES = ['press'];
    /* The most a warm waits for the first screen to settle, counted from the
       moment it may start waiting (the veil lifted, the screen presented). */
    const FIRST_SCREEN_SETTLE_CAP_MS = 8000;
    const BATTLE_PRELOAD = [
        'draw',
        'play',
        'summon',
        'magic',
        'damage',
        'heal',
        'turn_start',
        'error',
        'impact',
        'destroy',
        'win',
        'lose',
        'manga_reveal',
        'disconnect',
    ];

    function settledWarmState(ready) {
        return { settled: true, promise: Promise.resolve(ready), finish: null };
    }

    function beginVoiceWarmup(voice) {
        if (destroyed || !voice) return settledWarmState(false);
        voice.preload = 'auto';
        if (Number(voice.readyState) >= 3) {
            voiceWarmRetryLoads.delete(voice);
            return settledWarmState(true);
        }
        const existing = voiceWarmStates.get(voice);
        if (existing) return existing;

        let settle;
        const state = {
            settled: false,
            timeoutId: null,
            promise: new Promise((resolve) => { settle = resolve; }),
            finish: null,
        };
        voiceWarmStates.set(voice, state);
        activeVoiceWarmStates.add(state);

        const finish = (ready, failure = null) => {
            if (state.settled) return;
            state.settled = true;
            activeVoiceWarmStates.delete(state);
            if (state.timeoutId !== null) clearTimeout(state.timeoutId);
            voice.removeEventListener?.('canplay', onReady);
            voice.removeEventListener?.('canplaythrough', onReady);
            voice.removeEventListener?.('error', onError);
            /* The state is deleted on EVERY settlement, success included. It
               used to survive success, and the early `existing` return above
               then handed the stale resolved-true promise back forever — so a
               voice whose decoder Firefox later parked (readyState back below
               3) could never be load()ed again, preload() lied "ready" to
               deferPlay, and every pack after the first opened silent. A
               settled state's only job is sharing an IN-FLIGHT warm-up. */
            voiceWarmStates.delete(voice);
            if (!ready) {
                voiceWarmRetryLoads.set(voice, failure !== 'timeout');
            } else {
                voiceWarmRetryLoads.delete(voice);
            }
            settle(ready);
        };
        state.finish = finish;
        const onReady = () => finish(true);
        const onError = () => finish(false, 'error');
        voice.addEventListener?.('canplay', onReady, { once: true });
        voice.addEventListener?.('canplaythrough', onReady, { once: true });
        voice.addEventListener?.('error', onError, { once: true });
        if (Number(voice.readyState) >= 3) {
            finish(true);
            return state;
        }
        const shouldRequestLoad = voiceWarmRetryLoads.get(voice) !== false
            || Boolean(voice.error);
        voiceWarmRetryLoads.delete(voice);
        if (shouldRequestLoad) {
            try {
                voice.load?.();
            } catch (_) {
                finish(false, 'error');
                return state;
            }
        }
        if (Number(voice.readyState) >= 3) finish(true);
        else {
            state.timeoutId = setTimeout(
                () => finish(false, 'timeout'),
                VOICE_WARMUP_TIMEOUT_MS,
            );
        }
        return state;
    }

    function waitForVoiceWarmup(voice) {
        return beginVoiceWarmup(voice).promise;
    }

    function createVoice(url, level) {
        const voice = new Audio();
        // The buffer's own CORS mode, so both share one HTTP cache entry (the
        // asset host keys CORS and no-CORS copies apart with Vary: Origin).
        voice.crossOrigin = 'anonymous';
        voice.src = url;
        voice.volume = level;
        const playback = { busy: false, startedAt: 0, generation: 0 };
        voicePlaybackStates.set(voice, playback);
        const markIdleIfStopped = () => {
            // Media pause/ended events are queued tasks. A stolen voice can
            // already be playing its next cue by the time an event from the
            // old cue is delivered, so the event may retire the voice only
            // while the physical element still reports itself stopped.
            if (voice.paused === true || voice.ended === true) {
                playback.busy = false;
            }
        };
        voice.onended = markIdleIfStopped;
        voice.onpause = markIdleIfStopped;
        beginVoiceWarmup(voice);
        return voice;
    }

    function ensureSoundPool(name, source = soundSources[name]) {
        if (destroyed) return [];
        if (soundPools[name]?.length) return soundPools[name];
        const url = source || soundSources[name];
        if (!url) return [];
        const pool = Array.from(
            { length: VOICE_POOL_SIZE },
            () => createVoice(url, effectiveVolume()),
        );
        soundPools[name] = pool;
        poolCursors[name] = 0;
        return pool;
    }

    function warmSound(name, { waitForMedia = false } = {}) {
        if (destroyed) return Promise.resolve(false);
        const url = soundSources[name];
        if (!url) return Promise.resolve(false);
        /* ONE DOWNLOAD PER SOUND: a pool not built yet waits for the one
           download the buffer makes (AudioGainBus.sourceUrl), then its voices
           load the same URL in the same CORS mode, from the HTTP cache. Never a
           blob: URL — the page's media-src CSP refuses one. */
        const shareable = !soundPools[name]?.length && typeof AudioGainBus?.sourceUrl === 'function';
        let voicesBuilt;
        if (shareable) {
            voicesBuilt = null;
        } else {
            const voices = ensureSoundPool(name);
            voices.forEach(beginVoiceWarmup);
            voicesBuilt = Promise.resolve(voices);
        }
        let bufferReady;
        try {
            bufferReady = AudioGainBus?.preloadBuffer?.(url);
        } catch (_) {
            bufferReady = null;
        }
        if (shareable) {
            let shared;
            try {
                shared = AudioGainBus.sourceUrl(url);
            } catch (_) {
                shared = null;
            }
            voicesBuilt = Promise.resolve(shared).catch(() => null).then((source) => {
                if (destroyed) return [];
                const voices = ensureSoundPool(name, source || url);
                voices.forEach(beginVoiceWarmup);
                return voices;
            });
        }
        const mediaReady = waitForMedia
            ? voicesBuilt.then((voices) => Promise.all(voices.map(waitForVoiceWarmup)))
            : Promise.resolve(null);
        return Promise.all([
            mediaReady,
            Promise.resolve(bufferReady),
        ]).then(([voiceResults, buffer]) => {
            if (destroyed) return false;
            const gain = typeof soundGains[name] === 'number' ? soundGains[name] : 1;
            const level = Math.max(0, Math.min(1, effectiveVolume() * gain));
            const decodedBufferRequired = (
                AudioGainBus?.requiresGainBus?.() === true
                && level < 1
            );
            if (decodedBufferRequired) return Boolean(buffer);
            return Boolean(buffer)
                || (Array.isArray(voiceResults) && voiceResults.some(Boolean));
        });
    }

    /**
     * Resolves once the screen the player is looking at has settled: a
     * background slot of the one load scheduler (no foreground load in
     * flight, the boot veil's and the match's first-screen holds released),
     * or the player's next press (the next screen is near), or the cap.
     * Never rejects; the cue warm that waits on it is only ever later.
     */
    function afterFirstScreen(capMs = FIRST_SCREEN_SETTLE_CAP_MS) {
        return new Promise((resolve) => {
            let done = false;
            let timer = null;
            const finish = () => {
                if (done) return;
                done = true;
                clearTimeout(timer);
                document.removeEventListener('pointerdown', finish, true);
                document.removeEventListener('keydown', finish, true);
                resolve();
            };
            timer = setTimeout(finish, capMs);
            document.addEventListener('pointerdown', finish, true);
            document.addEventListener('keydown', finish, true);
            try {
                if (!settleScope || settleScope.closed) {
                    settleScope = LoadScheduler?.scope?.('sound-effects', { runsOn: '*' }) || null;
                }
                const slot = settleScope?.read?.('background-soon', () => true);
                if (slot && typeof slot.then === 'function') slot.then(finish, finish);
                else finish();
            } catch (_) {
                finish();
            }
        });
    }

    function warmBootCues(extra = []) {
        // The press tick (and any cue a surface asked for by name) now.
        preload([...new Set([...FIRST_SCREEN_CUES, ...extra])]).catch((err) => {
            console.warn('[SoundEffects] Failed to load sounds:', err);
        });
        // The rest of the boot cues once the first screen has settled.
        let released;
        try { released = BootVeil?.whenReleased?.(); } catch (_) { released = null; }
        Promise.resolve(released).catch(() => undefined)
            .then(() => afterFirstScreen())
            .then(() => (destroyed ? false : preload(BOOT_PRELOAD)))
            .catch((err) => {
                console.warn('[SoundEffects] Failed to load sounds:', err);
            });
    }

    function init() {
        if (destroyed || isInitialized) return;
        isInitialized = true;

        loadPreferences();
        window.addEventListener('storage', onStorage);

        if (MoonfallCloud && MoonfallCloud.isReady && MoonfallCloud.isReady()) {
            warmBootCues();
        } else {
            convexReadyListener = () => {
                convexReadyListener = null;
                // Surface entry can request a narrow warm-up before the cloud
                // manifest is ready. Keep those bounded names and replay them
                // with the boot profile once URLs become authoritative; a
                // failed cold request must not silently erase the next cue.
                warmBootCues([...requestedPreloads]);
            };
            window.addEventListener('moonfall:convex-ready', convexReadyListener, { once: true });
        }
        window.addEventListener('pagehide', stopAll);
        window.addEventListener('pageshow', rewarmAfterWake);
        document.addEventListener('visibilitychange', onVisibilityChange);

        if (window.CLIENT_DEBUG_MODE) console.log('Sound effects system initialized');
    }

    async function resolveAndLoadSounds() {
        if (destroyed) return false;
        if (!MoonfallCloud || !MoonfallCloud.sounds || !MoonfallCloud.isReady || !MoonfallCloud.isReady()) {
            console.warn('[SoundEffects] Convex not ready, cannot load sounds');
            return false;
        }

        const soundIds = Object.values(soundFiles);
        let soundUrls = null;

        try {
            soundUrls = await MoonfallCloud.sounds.getUrls(soundIds);
            if (destroyed) return false;
            usingConvexSounds = Object.values(soundUrls || {}).some((url) => !!url);
        } catch (error) {
            console.warn('[SoundEffects] Failed to load Convex sounds:', error);
            return false;
        }

        if (!usingConvexSounds) {
            console.warn('[SoundEffects] No sounds found in Convex');
            return false;
        }

        for (const name of Object.keys(soundFiles)) {
            if (destroyed) return false;
            const soundId = soundFiles[name];
            const url = soundUrls && soundUrls[soundId] ? soundUrls[soundId] : null;
            if (url) {
                soundSources[name] = url;
            }
        }

        if (window.CLIENT_DEBUG_MODE) console.log('[SoundEffects] Loaded sounds from Convex');
        return true;
    }

    function loadSounds() {
        if (destroyed) return Promise.resolve(false);
        if (!loadPromise) {
            loadPromise = resolveAndLoadSounds().then((loaded) => {
                if (!loaded) loadPromise = null;
                return loaded;
            }, (error) => {
                loadPromise = null;
                throw error;
            });
        }
        return loadPromise;
    }

    function preload(names = []) {
        if (destroyed) return Promise.resolve(false);
        const requested = Array.isArray(names) ? names : [names];
        // Called from pointerdown for game entry: create/resume the iOS context
        // before the asynchronous manifest lookup leaves the gesture task.
        try { AudioGainBus?.resume?.(); } catch (_) { }
        for (const name of requested) {
            if (Object.hasOwn(soundFiles, name)) requestedPreloads.add(name);
        }
        return loadSounds().then(async (loaded) => {
            if (!loaded || destroyed) return false;
            const readiness = await Promise.all(requested.map((name) => (
                warmSound(name, { waitForMedia: true })
            )));
            return !destroyed && readiness.every(Boolean);
        });
    }

    /**
     * Warm the battle cues. `deferUntil` (a promise) holds the DOWNLOADS back
     * until it settles — the match's first screen presented — while the audio
     * context is still resumed now, inside the gesture that asked (iOS only
     * unlocks audio there). Measured 25 September 2026 on a phone's first
     * visit: the 13 cues, each fetched by the buffer and two voices, were
     * competing with the board's art under the match loading screen.
     * @param {{ deferUntil?: Promise<unknown> }} [options]
     */
    function preloadBattle({ deferUntil } = {}) {
        if (!deferUntil) return preload(BATTLE_PRELOAD);
        try { AudioGainBus?.resume?.(); } catch (_) { }
        /* Presented is not settled: a first screen that presents at its cap
           still has pictures on the wire (round 15), so the cues also wait for
           those, or for the player's first press there. */
        return Promise.resolve(deferUntil).catch(() => undefined)
            .then(() => afterFirstScreen())
            .then(() => preload(BATTLE_PRELOAD));
    }

    function reportPlaybackFailure(name, error) {
        const message = `Failed to play ${name} sound:`;
        if (error?.name === 'NotAllowedError') {
            console.warn(message, error);
            return;
        }
        console.error(message, error);
    }

    function deferPlay(name, options) {
        const requestedWait = Number(options?.waitForLoadMs);
        if (
            !Object.hasOwn(soundFiles, name)
            || !Number.isFinite(requestedWait)
            || requestedWait <= 0
        ) {
            return false;
        }

        const expiresAt = Date.now() + Math.min(requestedWait, MAX_DEFERRED_PLAY_MS);
        let cancelled = false;
        void preload([name]).then((ready) => {
            if (ready && !cancelled && Date.now() <= expiresAt) {
                /* Carry the REMAINING window. A bare retry was a one-shot with
                   no window of its own: if the voice sagged again between the
                   warm resolving and this call, the cue was dropped inside the
                   very window the caller granted. */
                playSound(name, { waitForLoadMs: Math.max(1, expiresAt - Date.now()) });
            }
        }).catch((error) => {
            if (!cancelled) reportPlaybackFailure(name, error);
        });
        return () => { cancelled = true; };
    }

    function playSound(name, options) {
        const eff = effectiveVolume();
        const pool = soundPools[name];
        if (eff <= 0) return false;
        if (!pool?.length) return deferPlay(name, options);

        const gain = typeof soundGains[name] === 'number' ? soundGains[name] : 1;
        const level = Math.max(0, Math.min(1, eff * gain));

        // iOS ignores `.volume`, so the bus carries the requested gain whenever
        // its decoded buffer is ready. A cold or suspended bus declines
        // truthfully and never starts that request later. The warmed element
        // path remains valid only when its volume is controllable or the
        // requested level is already 100%.
        const bus = AudioGainBus;
        if (bus?.playBuffer?.(soundSources[name], level)) return true;
        // On iOS `.volume` is read-only. Falling through at a requested 35%
        // would blast the element at 100%; until the decoded bus is ready, an
        // omitted cue is the only truthful volume behavior.
        if (bus?.requiresGainBus?.() === true && level < 1) return false;

        try {
            const cursor = poolCursors[name] || 0;
            let sound = null;
            let selectedIndex = -1;
            for (let offset = 0; offset < pool.length; offset += 1) {
                const index = (cursor + offset) % pool.length;
                const candidate = pool[index];
                const readyState = Number(candidate?.readyState);
                const playback = voicePlaybackStates.get(candidate);
                const isIdle = playback?.busy !== true
                    || candidate.paused === true
                    || candidate.ended === true;
                if (isIdle && (!Number.isFinite(readyState) || readyState >= 3)) {
                    sound = candidate;
                    selectedIndex = index;
                    break;
                }
            }
            // Starting a cold media element queues the request and lets it
            // surface late, detached from the action that caused it. A missed
            // cue is less misleading than a ghost cue, so only a ready voice
            // may own this play.
            if (!sound) {
                const readyBusyVoices = pool
                    .map((candidate, index) => ({
                        candidate,
                        index,
                        playback: voicePlaybackStates.get(candidate),
                    }))
                    .filter(({ candidate }) => {
                        const readyState = Number(candidate?.readyState);
                        return !Number.isFinite(readyState) || readyState >= 3;
                    })
                    .sort((a, b) => (
                        (a.playback?.startedAt || 0) - (b.playback?.startedAt || 0)
                    ));
                const oldest = readyBusyVoices[0];
                if (!oldest) {
                    /* NO READY VOICE AT ALL. Firefox parks the decoder of an
                       idle, never-played media element, so a warmed pool can
                       quietly fall below HAVE_FUTURE_DATA while the player
                       navigates ("no card reveal sound. at least not
                       reliably"). A caller that granted a wait window gets a
                       deferral — re-warm and fire the cue the moment a voice
                       can actually sound; everyone else at least hears about
                       the drop in the console instead of a silent false.

                       RE-WARM EITHER WAY. Every one of this module's callers
                       passes no `waitForLoadMs`, so the deferral below is the
                       only repair and it was unreachable in production: a pool
                       that sagged once was dropped from then on, for the rest
                       of the page. This repairs the pool whether or not THIS
                       cue can still be played, so the next one is heard. It
                       cannot create a pool that does not exist (warmSound only
                       warms a resolved source) so the "a cold play does no work
                       now" contract is untouched. */
                    void warmSound(name);
                    const deferral = deferPlay(name, options);
                    if (deferral) return deferral;
                    console.warn(
                        `[SoundEffects] dropped '${name}': no pooled voice is ready `
                        + '(suspended decoders?) and the caller gave no waitForLoadMs window',
                    );
                    return false;
                }
                sound = oldest.candidate;
                selectedIndex = oldest.index;
                try { sound.pause?.(); } catch (_) { }
            }
            poolCursors[name] = (selectedIndex + 1) % pool.length;
            try { sound.currentTime = 0; } catch (_) { /* not seekable yet */ }
            sound.volume = level;
            const playback = voicePlaybackStates.get(sound);
            let generation = 0;
            if (playback) {
                playback.busy = true;
                playback.generation += 1;
                generation = playback.generation;
                playbackSequence += 1;
                playback.startedAt = playbackSequence;
            }

            let playPromise;
            try {
                playPromise = sound.play();
            } catch (error) {
                if (playback?.generation === generation) playback.busy = false;
                reportPlaybackFailure(name, error);
                return false;
            }
            if (playPromise !== undefined) {
                playPromise.catch((error) => {
                    // A rejected promise belongs to the play() call that
                    // created it, not necessarily to the cue now occupying
                    // this pooled element. Never let an obsolete rejection
                    // advertise a reused voice as idle.
                    if (playback?.generation === generation) playback.busy = false;
                    reportPlaybackFailure(name, error);
                });
            }
            return true;
        } catch (error) {
            reportPlaybackFailure(name, error);
            return false;
        }
    }

    /**
     * Re-warm the pools after the platform hands the audio session back.
     *
     * Locking the phone mid-SFX takes the iOS audio session away, and WebKit
     * can tear the pooled decoders down with it — so a pool that was warm
     * before the lock comes back below HAVE_FUTURE_DATA. This module listened
     * for `pagehide` (stopAll) and for NOTHING on the way back, so every later
     * cue found no ready voice and was dropped, exactly as reported: "after
     * unlock, SFX stay broken".
     *
     * beginVoiceWarmup() is the right instrument because it is already
     * idempotent: a voice still at readyState >= 3 settles immediately without
     * calling load(), so a healthy pool is never disturbed and the next cue is
     * never made late by the recovery itself.
     */
    function rewarmAfterWake() {
        if (destroyed) return;
        // The bus may be interrupted too; a suspended context plays silence.
        try { AudioGainBus?.resume?.(); } catch (_) { }
        for (const pool of Object.values(soundPools)) {
            for (const voice of pool || []) {
                if (voice) beginVoiceWarmup(voice);
            }
        }
    }

    // iOS fires pagehide/pageshow with NO visibilitychange (soundtrack.js
    // documents the sequence), so neither trigger alone covers the wake.
    function onVisibilityChange() {
        if (document.visibilityState === 'visible') rewarmAfterWake();
    }

    function stopAll() {
        try { AudioGainBus?.stopBuffers?.(); } catch (_) { }
        for (const pool of Object.values(soundPools)) {
            for (const voice of pool || []) {
                const playback = voicePlaybackStates.get(voice);
                const wasBusy = playback?.busy === true;
                if (playback) {
                    playback.generation += 1;
                    playback.busy = false;
                }
                if (wasBusy || voice.paused === false) {
                    try { voice.pause?.(); } catch (_) { }
                }
                try { voice.currentTime = 0; } catch (_) { }
            }
        }
        Object.keys(poolCursors).forEach((name) => {
            poolCursors[name] = 0;
        });
    }

    /**
     * Drop every warmed voice and the resolved sound map, keeping the module
     * alive. `destroy()` uses it too; the difference is that this one does not
     * set `destroyed`, so the next preload rebuilds the pools from scratch.
     *
     * A media element holds a decoded resource for as long as it holds a
     * source, so the src is dropped rather than just the reference.
     */
    function releaseVoicePools() {
        for (const state of activeVoiceWarmStates) state.finish?.(false);
        activeVoiceWarmStates.clear();
        stopAll();
        for (const pool of Object.values(soundPools)) {
            for (const voice of pool || []) {
                try {
                    voice.removeAttribute?.('src');
                } catch (_) { }
            }
        }
        Object.keys(soundPools).forEach((name) => delete soundPools[name]);
        Object.keys(soundSources).forEach((name) => delete soundSources[name]);
        Object.keys(poolCursors).forEach((name) => delete poolCursors[name]);
        requestedPreloads.clear();
        loadPromise = null;
        playbackSequence = 0;
    }

    /**
     * THE SEAM THIS MODULE WAS MISSING, and the reason one browser spec has
     * been red rather than green.
     *
     * `audio-ios-bus.spec.js` needs a SoundEffects whose voice pools are empty,
     * so it can count what a warm-up creates. It tried to get one by injecting
     * the source again as a classic `<script>` — which cannot work, because the
     * file is ESM: the tag never executes it, the already-bundled module
     * answers, and the spec measured voices warmed during the page's normal
     * boot. The spec's own comment named this fix and left the test failing
     * rather than loosening the assertion, which was the right call and is why
     * this exists.
     *
     * Not gated to tests: it only clears caches that the next `preload()`
     * rebuilds, exactly as `AudioGainBus._reset()` does.
     */
    function resetVoicePools() {
        if (destroyed) return false;
        releaseVoicePools();
        usingConvexSounds = false;
        return true;
    }

    function destroy() {
        if (destroyed) return;
        destroyed = true;
        document.removeEventListener('DOMContentLoaded', init);
        window.removeEventListener('storage', onStorage);
        window.removeEventListener('pagehide', stopAll);
        window.removeEventListener('pageshow', rewarmAfterWake);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        if (convexReadyListener) {
            window.removeEventListener('moonfall:convex-ready', convexReadyListener);
            convexReadyListener = null;
        }
        releaseVoicePools();
        try { settleScope?.close?.(); } catch (_) { }
        settleScope = null;
        if (window.__moonfallSoundEffectsDestroy === destroy) {
            delete window.__moonfallSoundEffectsDestroy;
        }
    }

    function getSoundNames() {
        return Object.keys(soundFiles);
    }

    // Load preferences synchronously at script load (so getVolume/isMuted are
    // correct immediately) and again on DOMContentLoaded as a safety net.
    document.addEventListener('DOMContentLoaded', init);
    init();
    window.__moonfallSoundEffectsDestroy = destroy;

    return {
        play: playSound,
        preload,
        preloadBattle,
        stopAll,
        destroy,
        resetVoicePools,
        setVolume,
        getVolume,
        getEffectiveVolume,
        setMuted,
        toggleMuted,
        isMuted,
        getSoundNames
    };
})();

export { SoundEffects };
